<template>
  <v-skeleton-loader
    v-if="loading"
    class="m-auto"
    type="table"
    transition="scale-transition"
  />
  <VTableRemote
    v-else-if="hasPermission('treatmentControls.view') && hasPermission('nodes.types.view')"
    ref="tableRemoteRef"
    :iconHeader="hasPermission('treatmentControls.add') ? 'add' : null"
    :items="displayedControls"
    :search="queryParams.search"
    isSearchable
    hideDefaultFooter
    :isInfinitScroll="true"
    :headers="tableHeaders"
    :itemActions="actions"
    :isCustomIcon="true"
    :title="$t('treatmentControls.pageName')"
    :centerColumns="['action']"
    :notSortableColumns="['action']"
    :sortableColumns="['name']"
    :pagination="pagination"
    @iconAction="onClickAction"
    @click-icon-header="onClickAdd"
    @sendQueryParams="onQueryParams"
    @search="onSearch"
  >
    <template #item.networkTypes="{ item }">
      <List :items="item.networkTypes" />
    </template>
    <template #withInfiniteScroll>
      <div v-intersect="endIntersect" />
    </template>
  </VTableRemote>
  <NoPermissionAlert
    v-else
    :message="$t('permissions.error')"
  />
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { cloneDeep, debounce } from 'lodash';
import TreatmentControlForm from '@/views/admin/configuration/treatmentControls/TreatmentControlForm';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import List from '@/components/shared/content-views/slots/List';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import perpage from '@/constants/pagination';

export default {
    components: {
        VTableRemote,
        List,
        NoPermissionAlert,
    },
    data: () => ({
        controls: [],
        pagination: {},
        queryParams: {
            page: 1,
            perpage,
            search: '',
            pagination: true,
        },
        loading: false,
    }),
    computed: {
        ...mapGetters([
            'lang',
            'hasPermission',
            'teamID',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [
                'name',
                'networkTypes',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        displayedControls() {
            return this.controls.map(control => ({
                id: control.id,
                name: control.name[this.lang] ? control.name[this.lang] : control.name[this.teamMainLang],
                networkTypes: control.networkTypes
                    .map(networkType => networkType.name[this.lang])
                    .sort((a, b) => a.localeCompare(b)),
            }));
        },
        actions() {
            const actions = [];
            if (this.hasPermission('treatmentControls.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }
            if (this.hasPermission('treatmentControls.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
    },
    watch: {
        'queryParams.search': function () {
            this.debouncedFetchQuery();
        },
    },
    created() {
        this.loading = true;
        this.getControls();
        this.loading = false;

        this.debouncedFetchQuery = debounce(this.fetchQuery, 300);
    },
    methods: {
        ...mapActions(['reloadTeam']),
        async fetchQuery() {
            this.controls = [];
            this.pagination = {};
            await this.getControls();
        },
        async getControls() {
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            const { data } = await this.$rq.getTreatmentControls(cleanQueryParams);
            this.controls = [...this.controls, ...data.treatmentControls];
            this.pagination = data.pagination;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('treatmentControls.add'),
                showLangs: true,
                component: Vue.component('TreatmentControlForm', TreatmentControlForm),
                onConfirm: requestPayload => this.addTreatmentControl(requestPayload),
            });
        },
        async onClickAction(treatmentControl, action) {
            if (!action && treatmentControl === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getTreatmentControl(treatmentControl.id);
                this.$root.$emit('showModal', {
                    title: this.$t('treatmentControls.update'),
                    showLangs: true,
                    component: Vue.component('TreatmentControlForm', TreatmentControlForm),
                    payload: { treatmentControl: cloneDeep(data), isEdition: true },
                    onConfirm: requestPayload => this.updateTreatmentControl(treatmentControl.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('treatmentControls.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('treatmentControls.confirmRemove') },
                    onConfirm: () => this.removeTreatmentControl(treatmentControl),
                    isForm: false,
                });
            }
        },
        async addTreatmentControl(requestPayload) {
            await this.$rq.addTreatmentControl(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('treatmentControls.added', { name: requestPayload.name[this.lang] }),
            });
            this.fetchQuery();
        },
        async updateTreatmentControl(id, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateTreatmentControl(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('treatmentControls.updated', { name: requestPayload.name[this.lang] }),
            });
            this.fetchQuery();
        },
        async removeTreatmentControl(treatmentControl) {
            await this.$rq.removeTreatmentControl(treatmentControl.id);
            this.$toasted.global.toast_success({
                message: this.$t('treatmentControls.removed', { name: treatmentControl.name[this.lang] }),
            });
            this.fetchQuery();
        },
        onQueryParams(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
            };
            this.fetchQuery();
        },
        onSearch(search) {
            this.queryParams.search = search;
            this.debouncedFetchQuery();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getControls();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
